import type { Field, QuestionDestination } from '../../shared/types';
import { copyQuestion } from '../../shared/questionIds';
import { questionSections } from '../../shared/questionRouting';

const header = (): Field => ({ id: crypto.randomUUID().slice(0, 8), type: 'section', label: '', required: false, descriptionEnabled: true });
function cleanDestinations(fields: Field[]): Field[] {
  const sections = questionSections(fields);
  return sections.flatMap((section, index) => {
    const later = new Set(sections.slice(index + 1).map(next => next.id));
    const destination = (target: QuestionDestination): QuestionDestination => typeof target === 'object' && !later.has(target.sectionId) ? 'next' : target;
    return section.fields.map(field => ({ ...field,
      ...(field.sectionDestination !== undefined ? { sectionDestination: destination(field.sectionDestination) } : {}),
      ...(field.routing ? { routing: { ...field.routing, targets: field.routing.targets.map(destination), other: field.routing.other ? destination(field.routing.other) : undefined } } : {}),
    }));
  });
}
const flatten = (groups: Field[][]) => groups.flatMap((group, i) => i > 0 && group[0]?.type !== 'section' ? [header(), ...group] : group);
export function duplicateSection(fields: Field[], index: number): { fields: Field[]; id: string } {
  const groups = questionSections(fields).map(section => section.fields);
  const source = groups[index];
  const ids = new Map(source.map(field => [field.id, crypto.randomUUID().slice(0, 8)]));
  const destination = (target: QuestionDestination) => typeof target === 'object' && ids.has(target.sectionId) ? { sectionId: ids.get(target.sectionId)! } : target;
  const copy = source.map(field => ({ ...copyQuestion(field, ids.get(field.id)!), ...(field.sectionDestination ? { sectionDestination: destination(field.sectionDestination) } : {}), ...(field.routing ? { routing: { ...field.routing, targets: field.routing.targets.map(destination), other: field.routing.other ? destination(field.routing.other) : undefined } } : {}) }));
  if (copy[0]?.type !== 'section') copy.unshift(header());
  groups.splice(index + 1, 0, copy);
  return { fields: cleanDestinations(flatten(groups)), id: copy[0].id };
}
export function moveSection(fields: Field[], index: number, to: number): Field[] {
  const order = questionSections(fields).map((_, i) => i);
  order.splice(index, 1); order.splice(to, 0, index);
  return reorderSections(fields, order);
}
/** `order` lists the current section indexes in their new order. */
export function reorderSections(fields: Field[], order: number[]): Field[] {
  const groups = questionSections(fields).map(section => section.fields);
  if (order.every((from, to) => from === to)) return fields;
  return cleanDestinations(flatten(order.map(from => groups[from])));
}
export function removeSection(fields: Field[], index: number, merge = false): Field[] {
  const groups = questionSections(fields).map(section => section.fields);
  if (merge && index === 0) return fields;
  const [removed] = groups.splice(index, 1);
  if (merge) groups[index - 1].push(...removed.filter(field => field.type !== 'section'));
  return cleanDestinations(flatten(groups));
}
